import { Alert, AlertDescription } from "@/ds/shadcn/components/ui/alert";
import { LoadingState } from "@/ds/custom/LoadingState";
import { Button } from "@/ds/shadcn/components/ui/button";
import { Spinner } from "@/ds/shadcn/components/ui/spinner";
import { useEmailVerify } from "@/features/auth/api/useEmailVerify";
import { useResendVerifyEmail } from "@/features/auth/api/useResendVerifyEmail";
import { AuthHeader } from "@/features/auth/components/AuthHeader";
import { AuthHeaderSubtitle } from "@/features/auth/components/AuthHeaderSubtitle";
import { AuthHeaderTitle } from "@/features/auth/components/AuthHeaderTitle";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { useEffectOnFirstMount } from "@/lib/common/useEffectOnFirstMount";
import { useQueryParams } from "@/lib/common/useQueryParams";
import { useTitle } from "@/lib/common/useTitle";
import { CheckCircle2Icon, CheckIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, type FC } from "react";
import { useNavigate } from "react-router";

export const PageEmailVerify: FC = () => {
	useTitle("Email Verification");

	const queryParams = useQueryParams();

	const code = queryParams.get("code");
	const userId = queryParams.get("userId");

	return code && userId
		? <PageEmailVerifyProcess code={code} userId={userId} />
		: <PageEmailVerifyPrompt />;
};

interface PageEmailVerifyProcessProps {
	code: string;
	userId: string;
}

const PageEmailVerifyProcess: FC<PageEmailVerifyProcessProps> = props => {
	const {
		code,
		userId
	} = props;

	const navigate = useNavigate();
	const emailVerify = useEmailVerify();
	const resendVerifyEmail = useResendVerifyEmail();
	const emailVerifyCodeExpired = useMemo(() => {
		return emailVerify.error && emailVerify.error.problem.problemType === "CodeExpired";
	}, [emailVerify.error]);

	useEffectOnFirstMount(() => {
		if (!code || !userId) {
			return;
		}

		emailVerify.mutate({
			userId: userId,
			emailVerifyCode: code
		});
	});

	useEffect(() => {
		if (!emailVerify.isSuccess) {
			return;
		}

		const timeout = setTimeout(() => navigate(".."), 2000);
		return () => clearTimeout(timeout);
	}, [emailVerify.isSuccess, navigate]);

	const onResendClick = useCallback(() => {
		resendVerifyEmail.mutate({ userId });
	}, [resendVerifyEmail, userId]);

	return (
		<>
			<AuthHeader>
				<AuthHeaderTitle>Email Verification</AuthHeaderTitle>
				<AuthHeaderSubtitle>
					{
						emailVerifyCodeExpired
							? <>Unfortunately, your email verification has expired!</>
							: <>Please wait, we&apos;re verifying your email address...</>
					}
				</AuthHeaderSubtitle>
			</AuthHeader>

			<div className="flex flex-col gap-4">
				{
					emailVerify.isPending &&
					<LoadingState />
				}

				{
					(resendVerifyEmail.error || emailVerify.isError) && (
						<ErrorDisplay error={resendVerifyEmail.error || emailVerify.error} />
					)
				}

				{
					emailVerify.isSuccess && (
						<Alert>
							<CheckCircle2Icon />
							<AlertDescription>
								We&apos;ve successfully verified your email address.
							</AlertDescription>
						</Alert>
					)
				}

				{
					resendVerifyEmail.isSuccess && (
						<Alert>
							<CheckIcon />
							<AlertDescription>
								We sent you a new verification email.
							</AlertDescription>
						</Alert>
					)
				}

				{
					emailVerifyCodeExpired && (
						<Button className="w-full" disabled={resendVerifyEmail.isPending || resendVerifyEmail.isSuccess} variant="outline" onClick={onResendClick}>
							{
								resendVerifyEmail.isPending
									? <Spinner />
									: "Resend Verification Email"
							}
						</Button>
					)
				}
			</div>
		</>
	);
};

const PageEmailVerifyPrompt: FC = () => {
	return (
		<>
			<AuthHeader>
				<AuthHeaderTitle>Email Verification</AuthHeaderTitle>
				<AuthHeaderSubtitle>
					We&apos;ve sent a verification link to your email address.
					Check your spam folder if you don&apos;t see it in your
					inbox.  You can close this tab after you verify your email.
				</AuthHeaderSubtitle>
			</AuthHeader>
		</>
	);
};
